import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IdeozLoading, showToast } from '@ideoz/ui';
import { useAppDispatch, authService, getUserProfile, saveAccessToken, saveRefreshToken, setShowOnboarding, getAccessToken } from '@ideoz/core';
import { GOOGLE_OAUTH_PENDING_KEY, GOOGLE_OAUTH_CALLBACK_PATH, type PendingGoogleLogin } from '../hooks/useGoogleAuth';
import { getSafeRedirect } from '../utils/safeRedirect';

/**
 * Google Sign-In Callback Page
 *
 * Landing point for the redirect-based Google Sign-In flow (see useGoogleAuth.ts).
 * Replaces the old popup + window.opener.postMessage flow, which breaks under
 * `Cross-Origin-Opener-Policy: same-origin` (required for WebContainer's
 * crossOriginIsolated) on any page that also hosts the WebContainer.
 *
 * Reads the authorization `code` from the URL, exchanges it for tokens via the
 * backend, then reproduces the post-login navigation logic that used to live in
 * useAuthDialog's Google onSuccess handler — reading the context (pairing key,
 * onboarding, `?redirect=`, conversation return) from the PendingGoogleLogin
 * stashed in sessionStorage before the redirect, since the page that triggered
 * sign-in is gone by the time this one runs.
 */
export const GoogleLoginCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return; // authorization codes are single-use
    ranRef.current = true;

    const run = async () => {
      const raw = sessionStorage.getItem(GOOGLE_OAUTH_PENDING_KEY);
      sessionStorage.removeItem(GOOGLE_OAUTH_PENDING_KEY);
      let pending: PendingGoogleLogin | null = null;
      try {
        pending = raw ? JSON.parse(raw) : null;
      } catch {
        pending = null;
      }
      const fallbackPath = pending?.originPathname || '/login';

      const oauthError = searchParams.get('error');
      const code = searchParams.get('code');
      const state = searchParams.get('state');

      if (oauthError) {
        showToast({
          message: oauthError === 'access_denied'
            ? 'Google sign-in was cancelled.'
            : `Google sign-in failed: ${oauthError}`,
          severity: 'error',
          type: 'error',
        });
        navigate(fallbackPath, { replace: true });
        return;
      }

      if (!code || !pending || !state || state !== pending.state) {
        if (getAccessToken()) {
          // Duplicate/replayed hit on this callback after sign-in already
          // completed on a prior run (e.g. a page refresh or HMR reload that
          // landed back on this URL after `google_oauth_pending` was already
          // consumed) — nothing to redo, and nothing actually failed.
          navigate('/', { replace: true });
          return;
        }
        showToast({ message: 'Google sign-in failed. Please try again.', severity: 'error', type: 'error' });
        navigate(fallbackPath, { replace: true });
        return;
      }

      try {
        const response = await authService.socialLoginWithGoogleCode({
          code,
          redirect_uri: `${window.location.origin}${GOOGLE_OAUTH_CALLBACK_PATH}`,
          pairing_key: pending.pairingKey ?? undefined,
          platform: pending.pairingKey ? 'plugin' : 'web',
        });

        if (!response.access_token) {
          throw new Error(response.message || 'Invalid social login response from server');
        }

        saveAccessToken(response.access_token);
        saveRefreshToken(response.refresh_token);
        (window as any).appAccessToken = response.access_token;

        const userData = await dispatch(getUserProfile(response.access_token)).unwrap();

        // Coming from the Figma plugin — pairing_key is the critical parameter.
        if (pending.pairingKey) {
          navigate(`/redirect?pairing_key=${pending.pairingKey}&figma_source=${pending.figmaSource || 'desktop'}`, { replace: true });
          return;
        }

        const isOnLandingPage = pending.originPathname === '/' || pending.originPathname === '/landing';
        const isOnBoardingShownRaw = userData?.metadata?.isOnBoardingShown ?? userData?.metadata?.is_onboarding_shown ?? false;
        const isOnBoardingShown = typeof isOnBoardingShownRaw === 'string'
          ? isOnBoardingShownRaw.toLowerCase() === 'true'
          : Boolean(isOnBoardingShownRaw);

        // Only show onboarding if first-time user on landing page.
        if (isOnBoardingShown === false && isOnLandingPage) {
          dispatch(setShowOnboarding(true));
          navigate('/onboarding', { replace: true });
          return;
        }

        if (isOnBoardingShown === false) {
          authService.updateOnboardingShown().catch((err) => {
            console.error('Failed to update onboarding shown flag:', err);
          });
        }

        // Return to the URL the user was trying to reach before login
        // (ProtectedRoute → `?redirect=`, carried via PendingGoogleLogin).
        const redirectTo = getSafeRedirect(pending.redirectTo);
        if (redirectTo) {
          navigate(redirectTo, { replace: true });
          return;
        }

        // Don't bounce the user away if they started sign-in from a conversation page.
        const isOnConversation = pending.originPathname === '/conversation' || pending.originPathname.startsWith('/conversation/');
        if (isOnConversation) {
          navigate(pending.originPathname, { replace: true });
          return;
        }
        const cameFromConversation = pending.originReferrer.includes('/conversation');
        if (cameFromConversation) {
          const conversationMatch = pending.originReferrer.match(/\/conversation[^?]*/);
          navigate(conversationMatch ? conversationMatch[0] : '/conversation', { replace: true });
          return;
        }

        navigate('/', { replace: true });
      } catch (error: any) {
        console.error('Google sign-in failed:', error);
        showToast({
          message: `Google sign-in failed: ${error?.message || 'Unknown error'}`,
          severity: 'error',
          type: 'error',
        });
        navigate(fallbackPath, { replace: true });
      }
    };

    run();
  }, [dispatch, navigate, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="min-h-screen flex items-center justify-center flex-col">
        <IdeozLoading size="lg" />
      </div>
      </div>
    </div>
  );
};

export default GoogleLoginCallback;
